<template>
  <generic-in-app-screen>
    <b-container>
      <h1 class="my-5">
        <b>{{ decrypted_user.username }} {{ $t('user_administration') }}</b>
      </h1>

      <h2 class="my-5">
        <b>{{ $t('rename_user') }}</b>
      </h2>

      <b-row>
        <b-col cols="6">
          <renamer
            v-model="local_username"
            :disabled="cur_user_is_default_admin_account"
          />
        </b-col>
      </b-row>

      <h2 class="my-5">
        <b>{{ $t('change_password') }}</b>
      </h2>

      <b-form-group :disabled="cur_user_is_default_admin_account">
        <b-form-group>
          <label>
            <b>{{ $t('old_password') }}</b>
          </label>
          <b-form-input v-model="old_password" type="password" />
        </b-form-group>
        <b-form-group>
          <label>
            <b>{{ $t('new_password') }}</b>
          </label>
          <b-form-input v-model="new_password" type="password" />
        </b-form-group>
        <b-form-group>
          <label>
            <b>{{ $t('password_confirmation') }}</b>
          </label>
          <b-form-input v-model="password_confirmation" type="password" />
        </b-form-group>
      </b-form-group>

      <h2 class="my-5">
        <b>{{ $t('modify_api_key') }}</b>
      </h2>

      <b-row>
        <b-col cols="6">
          <renamer v-model="local_api_key" />
        </b-col>
      </b-row>

      <h2 class="my-5">
        <b>{{ $t('user_platform_rights') }}</b>
      </h2>

      <div class="h4 ml-4">
        <b-badge>{{ global_group_from_id[cur_user.global_group_id] }}</b-badge>
      </div>

      <h2 class="my-5">
        <b>{{ $t('user_projects_rights') }}</b>
      </h2>

      <b-table
        class="synaplus-cosmo-scroll-higher-720"
        responsive
        striped
        hover
        :fields="projects_rights_fields"
        :items="projects_rights_items"
      />

      <validation-buttons-bar
        ref="validation_buttons_bar"
        :saved="!has_unsaved_changes"
        :is_validating="is_validating"
        @cancel="cancel"
        @save="save"
        @validate="validate"
      />
      <save-confirmation
        ref="password_change_confirmation"
        :optional_text="alert_message"
        :optional_variant="alert_variant"
      />
    </b-container>
  </generic-in-app-screen>
</template>

<i18n>
{
  "en": {
    "user_administration": "User administration",
    "rename_user": "Rename user",
    "modify_api_key": "Modify API key",
    "user_platform_rights": "User rights for platform",
    "user_projects_rights": "User rights per project"
  },
  "fr": {
    "user_administration": "Administration utilisateur",
    "rename_user": "Renommer l'utilisateur",
    "modify_api_key": "Modifier la clé API",
    "user_platform_rights": "Droits utilisateurs pour la plateforme",
    "user_projects_rights": "Droits utilisateurs par projet"
  }
}
</i18n>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import {
  subscription_mixin,
  client_encryption_mixin,
  validation_buttons_bar_wrapper_mixin,
} from '@/components/mixin.js'
import {
  update_user,
  generic_update,
  change_password,
} from '@/api/subscriptions.js'

import {
  global_auth_groups_enum,
  project_auth_groups_enum,
  global_group_from_id,
  project_group_from_id,
  notification_type_to_variant,
} from '@/utils.js'

import Renamer from '@/components/renamer.vue'
import SaveConfirmation from './save_confirmation.vue'
import GenericInAppScreen from '@/components/generic_in_app_screen.vue'
import ValidationButtonsBar from '@/components/validation_buttons_bar.vue'

export default {
  name: 'UserAdmin',
  components: {
    Renamer,
    SaveConfirmation,
    GenericInAppScreen,
    ValidationButtonsBar,
  },
  mixins: [
    subscription_mixin,
    client_encryption_mixin,
    validation_buttons_bar_wrapper_mixin,
  ],
  data() {
    return {
      local_username: '',
      local_api_key: '',

      old_password: '',
      new_password: '',
      password_confirmation: '',

      alert_message: '',
      alert_variant: '',

      cancel_path: '/',
    }
  },
  computed: {
    cur_user() {
      return (
        this.automl_users_undeleted_object.users[this.authenticated_user_id] ||
        {}
      )
    },
    decrypted_user() {
      let o = {}
      for (let [key, value] of _.entries(this.cur_user)) {
        if (['username', 'api_key'].includes(key)) {
          o[key] = this.client_decrypt_string(value)
        } else {
          o[key] = value
        }
      }
      return o
    },
    cur_user_is_default_admin_account() {
      return this.decrypted_user.username === 'admin'
    },
    entered_new_password() {
      return _.every(
        [this.old_password, this.new_password, this.password_confirmation],
        elt => elt !== ''
      )
    },
    projects_rights_for_cur_user() {
      return _.filter(
        this.automl_project_rights_undeleted_object.project_rights,
        elt => elt.user_id === this.authenticated_user_id
      )
    },

    projects_rights_fields() {
      return [
        { key: 'project_name', label: this.$t('project_name') },
        { key: 'project_rights', label: this.$t('project_rights') },
      ]
    },

    global_group_from_id() {
      return global_group_from_id
    },
    project_group_from_id() {
      return project_group_from_id
    },

    projects_rights_items() {
      let self = this
      let res = _.map(this.projects_rights_for_cur_user, function(elt) {
        return {
          project_name:
            self.automl_projects_undeleted_object.projects[elt.project_id].name,
          project_rights: self.$t(
            self.project_group_from_id[elt.project_group_id]
          ),
        }
      })
      return res
    },
    adapted_data() {
      let adapted = {
        uid: this.authenticated_user_id,
        username: this.local_username,
        api_key: this.local_api_key,
      }
      return adapted
    },
    has_unsaved_changes() {
      return (
        !_.isMatchWith(this.decrypted_user, this.adapted_data, _.isEqual) ||
        this.entered_new_password
      )
    },
    ...mapState(['authenticated_user_id']),
    ...mapGetters([
      'automl_users_undeleted_object',
      'automl_projects_undeleted_object',
      'automl_project_rights_undeleted_object',
    ]),
  },
  watch: {
    decrypted_user: {
      immediate: true,
      handler(new_val) {
        this.local_username = new_val.username || ''
        this.local_api_key = new_val.api_key || ''
      },
    },
  },
  beforeMount() {
    this.fetch_collections(['users', 'project_rights'])
  },
  beforeRouteLeave(to, from, next) {
    this.$refs.validation_buttons_bar.check_unsaved_changes(next)
  },

  methods: {
    // decrypt(s) {
    //   return this.client_decrypt_string(s)
    // },
    async validate_password_change() {
      if (this.new_password === this.password_confirmation) {
        let res = await change_password(
          this.authenticated_user_id,
          this.old_password,
          this.new_password
        )
        if (res) {
          this.alert_message = this.$t('password_change_success')
          this.alert_variant = 'success'
        } else {
          this.alert_message = this.$t('password_change_error')
          this.alert_variant = 'danger'
        }
      } else {
        this.alert_message = this.$t('password_confirmation_error')
        this.alert_variant = 'warning'
      }
      this.old_password = ''
      this.new_password = ''
      this.password_confirmation = ''
      this.$refs.password_change_confirmation.show()
    },
    async save_function() {
      let updated_user = {
        user_id: this.cur_user.uid,
      }
      let need_update = false

      if (this.local_username !== this.decrypted_user.username) {
        updated_user['username'] = this.local_username
      }
      if (this.local_api_key !== this.decrypted_user.api_key) {
        updated_user['api_key'] = this.local_api_key
      }

      let notifs = await update_user(updated_user)
      if (this.entered_new_password) {
        await this.validate_password_change()
      }
    },
  },
}
</script>
